import "./userPage.css";

import { BASIC_ROLES, ROLES } from "../constants";
import React, { useEffect, useState } from "react";

import { Button } from "reactstrap";
import { InlineSpinner } from "../components/InlineSpinner";
import { Prompt } from "react-router-dom";

// The campaign access grid, shared by the user page (one user, its campaigns) and the
// campaign page of the administration (one campaign, its users). Both draw one row per
// assignment and one column per role, with the same presets, bulk bar and save bar.

// Column headers and chips have to stay narrow, so each role gets a short name here and
// keeps its full label in the `title`. A role added to ROLES without an entry simply
// shows its full label, it never disappears from the grid.
const SHORT_LABELS = {
  view_features: "Results",
  view_experiments: "Plot map",
  view_monitoring: "Monitoring",
  view_analytics: "Analytics",
  view_pca: "PCA",
  view_anova: "ANOVA",
  create_site: "Create site",
  rate_plots: "Rate plots",
  edit_plot_rating_config: "Rating config",
};

// Columns are ordered by how often each role is actually granted in production, not by
// the declaration order of ROLES: view_experiments and view_features sit on nearly every
// assignment, edit_plot_rating_config on a quarter of them.
const ROLE_ORDER = [
  "view_experiments",
  "view_features",
  "view_monitoring",
  "view_analytics",
  "rate_plots",
  "view_pca",
  "view_anova",
  "create_site",
  "edit_plot_rating_config",
];

const ALL_ROLE_VALUES = ROLES.map(({ value }) => value);

// Half of the assignments in production are exactly one of these sets, so a row offers
// them whole rather than asking for seven or nine clicks.
const PRESETS = [
  { key: "basic", label: "Basic", roles: BASIC_ROLES },
  { key: "full", label: "Full", roles: ALL_ROLE_VALUES },
];

const rank = (value) => {
  const index = ROLE_ORDER.indexOf(value);
  return index === -1 ? ROLE_ORDER.length : index;
};

const ORDERED_ROLES = [...ROLES].sort((a, b) => rank(a.value) - rank(b.value));

// What each access actually opens, traced through the guards that enforce it. Analytics,
// PCA and ANOVA are worded as "show the tab" on purpose: they are checked by the frontend
// only, no Rails authorization backs them, so they hide a screen, they do not protect the
// data behind it.
const ROLE_HELP = {
  view_features:
    "Read the measured results of the campaign: trait values on the map and the detail of a trial. Analytics, PCA and ANOVA need it: removing it removes them.",
  view_experiments:
    "Read the plot map of the campaign: microplot geometry on the map and the Field map column.",
  view_monitoring:
    "Read mission monitoring: the Monitoring tab, a mission detail and its discussion.",
  view_analytics: "Show the Analytics tab and the analytics mode of the map.",
  view_pca: "Show the PCA dashboard inside Analytics.",
  view_anova: "Show the ANOVA dashboard inside Analytics.",
  create_site: "Create a site in the campaign, and edit the existing ones.",
  rate_plots: "Record plot ratings. Reading existing ratings does not need it.",
  edit_plot_rating_config:
    "Change the plot rating configuration of the campaign.",
};

// Fed to the HelpTooltip the analytics dashboards already use: one question mark, the
// nine accesses read side by side. A mark per column would widen all nine of them, since
// a role column is sized by its header.
export const ACCESS_HELP = {
  id: "helper-campaign-access",
  title: "What each access opens",
  content: (
    <>
      {/* Stated first because it is what surprises people: uploading is not one of the
          nine accesses. Client::UploadSessionsController#create only checks
          user_has_contract_info_access?, which is membership plus an active campaign. */}
      <p className="user-access__helpnote">
        Being assigned to an active campaign is already enough to upload data
        through its upload sessions, whatever the accesses below. They add to
        it, none of them is needed for uploading.
      </p>

      <dl className="user-access__help">
        {ORDERED_ROLES.map((role) => (
          <React.Fragment key={role.value}>
            <dt>{role.label}</dt>
            <dd>{ROLE_HELP[role.value]}</dd>
          </React.Fragment>
        ))}
      </dl>
    </>
  ),
  tips: [],
};

// Checkbox, name, row tools, one per role.
export const COLUMNS = ORDERED_ROLES.length + 3;

export const rolesKey = (list) => [...(list ?? [])].sort().join(",");

export const toggleIn = (list, value) =>
  list.includes(value)
    ? list.filter((item) => item !== value)
    : [...list, value];

// Analytics, PCA and ANOVA are built on the results, served on view_features only:
// granting one of them grants Results, revoking Results revokes them.
const ANALYTICS_ROLES = ["view_analytics", "view_pca", "view_anova"];

const withDependencies = (roleValues, granted) => {
  if (granted && roleValues.some((role) => ANALYTICS_ROLES.includes(role)))
    return [...roleValues, "view_features"];
  if (!granted && roleValues.includes("view_features"))
    return [...roleValues, ...ANALYTICS_ROLES];
  return roleValues;
};

// The draft both grids edit, keyed by the id of a row: a campaign on the user page, a
// user on the campaign page. `null` until the first click, so the rows follow the server
// until then.
export const useRolesDraft = (initialRoles) => {
  const [draft, setDraft] = useState(null);

  const edit = (update) =>
    setDraft((prevState) => update({ ...(prevState ?? initialRoles) }));

  return {
    roles: draft ?? initialRoles,
    edit,
    reset: () => setDraft(null),
    // Toggling one role on one row, and the bulk "add this role" of the selection.
    apply: (ids, roleValues, granted) =>
      edit((next) => {
        ids.forEach((id) => {
          const current = new Set(next[id] ?? []);
          withDependencies(roleValues, granted).forEach((role) =>
            granted ? current.add(role) : current.delete(role)
          );
          next[id] = [...current];
        });
        return next;
      }),
    // Presets replace instead of adding: "put these 15 rows on Basic" has to land on
    // Basic, not on Basic plus whatever each of them already carried.
    setRoles: (ids, roleValues) =>
      edit((next) => {
        ids.forEach((id) => {
          next[id] = [...roleValues];
        });
        return next;
      }),
  };
};

const labelOf = (value) =>
  SHORT_LABELS[value] ??
  ROLES.find((role) => role.value === value)?.label ??
  value;

export const RoleHeadCells = () =>
  ORDERED_ROLES.map((role) => (
    <th
      className="user-access__rolehead"
      key={role.value}
      title={`${role.label}. ${ROLE_HELP[role.value] ?? ""}`}
    >
      {labelOf(role.value)}
    </th>
  ));

// One cell per role. The cell says yes or no and nothing else, the name of the role
// lives in the sticky header above it.
export const RoleCells = ({ list, name, onToggle }) =>
  ORDERED_ROLES.map((role) => {
    const granted = list.includes(role.value);

    return (
      <td className="user-access__cell" key={role.value}>
        <button
          type="button"
          className={"user-access__box" + (granted ? " is-on" : "")}
          aria-pressed={granted}
          aria-label={`${role.label} on ${name}`}
          title={role.label}
          onClick={() => onToggle(role.value, !granted)}
        />
      </td>
    );
  });

// The presets sit between the name and its roles, where the eye already is. Parked
// after the nine columns they were a screen away from the name, and the horizontal
// scroll cut them off.
export const RowTools = ({ name, onPreset, unassign }) => (
  <td className="user-access__rowtools">
    {PRESETS.map((preset) => (
      <button
        type="button"
        key={preset.key}
        title={`Grant ${preset.label} on ${name}`}
        onClick={() => onPreset(preset.roles)}
      >
        {preset.label}
      </button>
    ))}

    {unassign && (
      <button type="button" title={unassign.title} onClick={unassign.onClick}>
        {unassign.label}
      </button>
    )}
  </td>
);

// Between the filters and the rows, only while a selection exists.
export const BulkBar = ({
  count,
  noun,
  onPreset,
  onAddRole,
  unassign,
  onDeselect,
}) => {
  const [picker, setPicker] = useState(false);

  return (
    <div className="user-access__bulk">
      <span className="user-access__bulkcount">
        {count} {noun}
        {count > 1 ? "s" : ""} selected
      </span>

      {PRESETS.map((preset) => (
        <Button
          key={preset.key}
          size="sm"
          color="primary"
          outline
          onClick={() => onPreset(preset.roles)}
        >
          {preset.label}
        </Button>
      ))}

      <Button
        size="sm"
        color="link"
        onClick={unassign.onClick}
        title={unassign.title}
      >
        {unassign.label}
      </Button>

      <Button size="sm" color="link" onClick={() => setPicker(!picker)}>
        {picker ? "Hide roles" : "Add a role"}
      </Button>

      <Button size="sm" color="link" onClick={onDeselect}>
        Deselect
      </Button>

      {picker && (
        <div className="user-access__bulkpicker">
          {ORDERED_ROLES.map((role) => (
            <button
              type="button"
              key={role.value}
              className="user-access__bulkrole"
              title={`Add ${role.label} to the selected ${noun}s`}
              onClick={() => onAddRole(role.value)}
            >
              {labelOf(role.value)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Outside the scrolling area, so it sits at the bottom of the page and not at the bottom
// of the screen: the list shrinks by its height instead of running underneath it, and
// the last row stays readable.
export const SaveBar = ({
  count,
  noun,
  onReview,
  onCancel,
  onSave,
  saving,
}) => (
  <div className="user-page__savebar">
    <span>
      {count} {noun}
      {count > 1 ? "s" : ""} modified
    </span>
    {onReview && (
      <Button size="sm" color="link" onClick={onReview}>
        Review
      </Button>
    )}
    <Button size="sm" color="link" onClick={onCancel} disabled={saving}>
      Cancel
    </Button>
    <Button size="sm" color="primary" onClick={onSave} disabled={saving}>
      {saving ? <InlineSpinner text="Saving..." /> : `Save (${count})`}
    </Button>
  </div>
);

// Asks before a draft is thrown away: `Prompt` for a navigation inside the app,
// `beforeunload` for a reload or a closed tab, where browsers show their own wording.
export const LeaveGuard = ({ when }) => {
  useEffect(() => {
    if (!when) return undefined;

    // Both: `returnValue` is what Chrome and Edge before 119 still read
    const onBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [when]);

  return (
    <Prompt
      when={when}
      message="You have unsaved access changes. Leave anyway?"
    />
  );
};
